import { useCallback, useMemo, useState } from "react";
import { AtSign, Bell, BellOff, CalendarClock, CheckCheck, ListChecks, Loader2, MessageSquareReply, MessageSquareText, PhoneOff, Search, SmilePlus, UserPlus, X } from "@fg/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { connectParityApi, type Notification } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Time grouping helpers
// ─────────────────────────────────────────────────────────────────────────────

type TimeGroup = "Today" | "Yesterday" | "This week" | "Earlier";

function getTimeGroup(dateStr: string): TimeGroup {
    const date = new Date(dateStr);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday.getTime() - 86_400_000);
    const startOfThisWeek = new Date(startOfToday.getTime() - startOfToday.getDay() * 86_400_000);

    if (date >= startOfToday) return "Today";
    if (date >= startOfYesterday) return "Yesterday";
    if (date >= startOfThisWeek) return "This week";
    return "Earlier";
}

const GROUP_ORDER: TimeGroup[] = ["Today", "Yesterday", "This week", "Earlier"];

// ─────────────────────────────────────────────────────────────────────────────
// Notification kinds → colour + icon, and the filter chips
// ─────────────────────────────────────────────────────────────────────────────

const KIND_STYLE: Record<string, { tile: string; Icon: typeof Bell; label: string }> = {
    mention: { tile: "bg-primary", Icon: AtSign, label: "Mention" },
    reply: { tile: "bg-emerald-600", Icon: MessageSquareReply, label: "Reply" },
    reaction: { tile: "bg-amber-500", Icon: SmilePlus, label: "Reaction" },
    join: { tile: "bg-sky-600", Icon: UserPlus, label: "Team" },
    post: { tile: "bg-indigo-500", Icon: MessageSquareText, label: "New post" },
    missed_call: { tile: "bg-red-600", Icon: PhoneOff, label: "Missed call" },
    dm: { tile: "bg-violet-600", Icon: MessageSquareReply, label: "Message" },
    meeting_invite: { tile: "bg-orange-500", Icon: CalendarClock, label: "Meeting" },
    // An action item in a meeting's notes was assigned to you.
    action_item: { tile: "bg-teal-600", Icon: ListChecks, label: "Action item" }
};

type Filter = "all" | "unread" | "mention" | "reply" | "reaction" | "meeting_invite" | "missed_call" | "action_item";

const FILTERS: Array<{ id: Filter; label: string }> = [
    { id: "all", label: "All" },
    { id: "unread", label: "Unread" },
    { id: "mention", label: "@Mentions" },
    { id: "reply", label: "Replies" },
    { id: "reaction", label: "Reactions" },
    { id: "meeting_invite", label: "Meetings" },
    { id: "missed_call", label: "Missed calls" },
    { id: "action_item", label: "Action items" }
];

// ─────────────────────────────────────────────────────────────────────────────
// ActivityFeedView
// ─────────────────────────────────────────────────────────────────────────────

interface ActivityFeedViewProps {
    /** Called when the user clicks a notification that targets a channel. */
    onOpenChannel?: (channelId: string, messageId?: string) => void;
    /** Called when the user clicks a notification that targets a DM (with its id when known). */
    onOpenDm?: (chatId?: string) => void;
    /** Called when the user clicks a notification that targets a group conversation (with its id when known). */
    onOpenConversation?: (conversationId?: string) => void;
    /** Called when the user clicks a notification from a meeting's chat (with the meeting id). */
    onOpenMeetingChat?: (meetingId?: string) => void;
    /** Called for "added you to a team" entries. */
    onOpenTeam?: (teamId?: string) => void;
}

/**
 * Teams' Activity: every notification grouped by day, with filter chips
 * (Unread, @Mentions, Replies, Reactions, Meetings) in place of a single
 * "Unread only" checkbox, coloured icons per kind, and a clear unread state.
 */
export function ActivityFeedView({ onOpenChannel, onOpenDm, onOpenConversation, onOpenMeetingChat, onOpenTeam }: ActivityFeedViewProps) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const [filter, setFilter] = useState<Filter>("all");
    const [query, setQuery] = useState("");

    const notifications = useQuery({
        queryKey: ["notifications", false],
        queryFn: () => connectParityApi.listNotifications(false, token),
        refetchInterval: 30_000,
    });

    const markRead = useMutation({
        mutationFn: (id: string) => connectParityApi.markNotificationRead(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
    });

    const markAllRead = useMutation({
        mutationFn: () => connectParityApi.markAllNotificationsRead(token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
    });

    const all = useMemo(() => notifications.data ?? [], [notifications.data]);
    const unreadCount = all.filter((n) => !n.isRead).length;
    const countFor = (f: Filter) =>
        f === "all" ? all.length : f === "unread" ? unreadCount : all.filter((n) => n.type === f).length;

    const grouped = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const items = all.filter((n) =>
            (filter === "all" ? true : filter === "unread" ? !n.isRead : n.type === filter)
            && (!needle || n.message.toLowerCase().includes(needle)));
        const buckets: Partial<Record<TimeGroup, Notification[]>> = {};
        for (const n of items) {
            (buckets[getTimeGroup(n.createdAt)] ??= []).push(n);
        }
        return GROUP_ORDER.map((g) => ({ group: g, items: buckets[g] ?? [] })).filter((b) => b.items.length > 0);
    }, [all, filter, query]);

    const handleClick = useCallback((n: Notification) => {
        if (!n.isRead) markRead.mutate(n.id.toString());
        if (!n.linkUrl) return;
        if (n.linkUrl.startsWith("/channels/")) {
            const parts = n.linkUrl.split("/");
            const channelId = parts[2];
            const messageId = parts[4];
            if (channelId) onOpenChannel?.(channelId, messageId);
        } else if (n.linkUrl.startsWith("/dms/")) {
            // "/dms/<chatId>" — a chat @mention opens that chat.
            onOpenDm?.(n.linkUrl.split("/")[2] || undefined);
        } else if (n.linkUrl.startsWith("/conversations/")) {
            onOpenConversation?.(n.linkUrl.split("/")[2] || undefined);
        } else if (n.linkUrl.startsWith("/meetings/")) {
            // "/meetings/<meetingId>" — an @mention in a meeting's chat opens that chat.
            onOpenMeetingChat?.(n.linkUrl.split("/")[2] || undefined);
        } else if (n.linkUrl.startsWith("/teams/")) {
            onOpenTeam?.(n.linkUrl.split("/")[2] || undefined);
        }
    }, [markRead, onOpenChannel, onOpenDm, onOpenConversation, onOpenMeetingChat, onOpenTeam]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <header className="shrink-0 border-b px-4 pb-3 pt-3">
                <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold">Activity</h1>
                    {unreadCount > 0 ? (
                        <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">{unreadCount}</span>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => markAllRead.mutate()}
                        disabled={markAllRead.isPending || unreadCount === 0}
                        title="Mark all as read"
                        className="ml-auto flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:text-muted-foreground disabled:hover:bg-transparent"
                    >
                        {markAllRead.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
                        Mark all read
                    </button>
                </div>
                <div className="relative mt-2">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search activity"
                        aria-label="Search activity"
                        className="h-8 w-full rounded-md border bg-background pl-7 pr-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5" role="tablist" aria-label="Filter activity">
                    {FILTERS.map((f) => {
                        const active = filter === f.id;
                        const n = countFor(f.id);
                        return (
                            <button
                                key={f.id}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setFilter(f.id)}
                                className={cn(
                                    "flex h-7 items-center gap-1 rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    active ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:border-primary/40 hover:bg-accent"
                                )}
                            >
                                {f.label}
                                {f.id !== "all" && n > 0 ? (
                                    <span className={cn("rounded-full px-1.5 text-[10px]", active ? "bg-white/25" : "bg-muted")}>{n}</span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            </header>

            <div className="min-h-0 flex-1 overflow-auto">
                {notifications.isPending ? (
                    <div className="flex h-32 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                ) : notifications.isError ? (
                    <p className="p-6 text-center text-sm text-destructive">Couldn't load your activity. Retrying…</p>
                ) : grouped.length === 0 ? (
                    <div className="flex h-56 flex-col items-center justify-center gap-3 text-center text-muted-foreground">
                        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <BellOff className="h-7 w-7" />
                        </span>
                        <p className="text-sm">
                            {query.trim() ? "No activity matches your search." : filter === "all" ? "You're all caught up." : "Nothing here right now."}
                            <br />
                            <span className="text-xs">Mentions, replies, reactions, meeting invites and team changes show up here for 30 days.</span>
                        </p>
                    </div>
                ) : (
                    grouped.map(({ group, items }) => (
                        <section key={group} aria-label={group}>
                            <div className="sticky top-0 z-10 bg-background/90 px-4 py-1.5 backdrop-blur-sm">
                                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{group}</span>
                            </div>
                            <ul>
                                {items.map((n) => (
                                    <NotificationRow
                                        key={n.id.toString()}
                                        notification={n}
                                        onClick={() => handleClick(n)}
                                        onDismiss={() => markRead.mutate(n.id.toString())}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Single notification row
// ─────────────────────────────────────────────────────────────────────────────

function NotificationRow({
    notification: n,
    onClick,
    onDismiss,
}: {
    notification: Notification;
    onClick: () => void;
    onDismiss: () => void;
}) {
    const style = KIND_STYLE[n.type] ?? { tile: "bg-slate-500", Icon: Bell, label: "Update" };
    const timeLabel = (() => {
        const d = new Date(n.createdAt);
        const diff = Date.now() - d.getTime();
        if (diff < 60_000) return "Just now";
        if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m`;
        if (diff < 86_400_000) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    })();
    const preview = n.message.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

    return (
        <li className={cn("group relative flex items-start gap-3 px-4 py-3 transition-colors hover:bg-accent/60", !n.isRead && "bg-primary/[0.04]")}>
            {!n.isRead ? <span aria-label="Unread" className="absolute bottom-3 left-0 top-3 w-[3px] rounded-r-full bg-primary" /> : null}
            <span className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-sm", style.tile)} aria-hidden>
                <style.Icon className="h-4 w-4" />
            </span>
            <button type="button" className="min-w-0 flex-1 text-left focus-visible:outline-none" onClick={onClick}>
                <span className="flex items-baseline gap-2">
                    <span className={cn("text-[11px] font-semibold uppercase tracking-wide", !n.isRead ? "text-primary" : "text-muted-foreground")}>{style.label}</span>
                    <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{timeLabel}</span>
                </span>
                <span className={cn("mt-0.5 line-clamp-2 block text-sm", !n.isRead ? "font-medium text-foreground" : "text-muted-foreground")}>{preview}</span>
            </button>
            {!n.isRead ? (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                    title="Mark as read"
                    aria-label="Mark as read"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded opacity-0 hover:bg-muted focus-visible:opacity-100 group-hover:opacity-100"
                >
                    <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
            ) : null}
        </li>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Badge count hook — exported so ConnectShell can render it on the rail
// ─────────────────────────────────────────────────────────────────────────────

export function useUnreadNotificationCount(): number {
    const token = useAccessToken();
    const { data } = useQuery({
        queryKey: ["notifications", false],
        queryFn: () => connectParityApi.listNotifications(false, token),
        refetchInterval: 30_000,
        staleTime: 20_000,
    });
    return (data ?? []).filter((n) => !n.isRead).length;
}
